import { useQuery } from "@tanstack/react-query";
import { getItems } from "../api/items";
import { ChartSeriesConfig } from "./types";

// Cor de cada técnico vem da ficha em `technicians.color` (hex, definida no Directus). As séries
// dinâmicas por pessoa das dashboards são chaveadas pelo UUID do utilizador (technicians.user_id),
// por isso mapeamos user_id -> cor e recolorimos as séries por chave.
export const TECHNICIAN_COLOR_FIELDS = "id,color,user_id";

const FIVE_MINUTES = 5 * 60 * 1000;

type TechnicianColorRecord = {
    color?: string | null;
    user_id?: string | { id?: string | null } | null;
};

const userIdOf = (userId: TechnicianColorRecord["user_id"]) => {
    const resolvedUserId = userId !== null && typeof userId === "object" ? userId.id : userId;
    return resolvedUserId;
};

// Mapa userUuid -> cor. Técnicos sem cor definida (null) ficam de fora para o gráfico cair na paleta de reserva.
export const buildTechnicianColorByUserId = (technicians: Array<TechnicianColorRecord>) => {
    const colorByUserId: Record<string, string> = {};
    technicians.forEach((technician) => {
        const userId = userIdOf(technician.user_id);
        if (!userId || !technician.color) return;
        colorByUserId[String(userId)] = technician.color;
    });
    return colorByUserId;
};

// Recolore as séries por pessoa com a cor do técnico quando existe; mantém a cor original — paleta de
// reserva (técnico sem cor) ou cor fixa das linhas agregadas (Total / Média da equipa) — quando a chave não corresponde.
export const applyTechnicianSeriesColors = (series: Array<ChartSeriesConfig>, colorByUserId: Record<string, string>) => {
    const recoloredSeries = series.map((serie) => {
        const color = colorByUserId[serie.key] || serie.color;
        const nextSerie = { ...serie, color };
        return nextSerie;
    });
    return recoloredSeries;
};

// Obtém as cores dos técnicos ao vivo (chave própria para não colidir com outros fetches de `technicians`).
// Se o visualizador não tiver leitura em `technicians`, devolve mapa vazio e os gráficos usam a paleta de reserva.
export const useTechnicianColors = () => {
    const techniciansResult = useQuery({
        queryKey: ["dashboard-technician-colors", TECHNICIAN_COLOR_FIELDS],
        queryFn: () => getItems("technicians", TECHNICIAN_COLOR_FIELDS, { pageSize: 300, sort: "id" }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
    });
    const technicianColorByUserId = buildTechnicianColorByUserId(techniciansResult.data?.items || []);
    const result = { technicianColorByUserId, isLoading: techniciansResult.isLoading };
    return result;
};
